import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { FC, Fragment } from 'react';
import { IRecordGetResponse } from 'src/models';

interface Props {
  data: { year: string; records: IRecordGetResponse[] }[] | null;
  selectedRecordsForDelete: readonly string[];

  page: number;
  rowsPerPage: number;
  total: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  setSelectedRecordsForDelete: (selected: readonly string[]) => void;
  recordSelected: (record: IRecordGetResponse) => void;
}

export const RecordsTable: FC<Props> = ({
  data,
  selectedRecordsForDelete,
  page,
  rowsPerPage,
  total,
  setPage,
  setRowsPerPage,
  setSelectedRecordsForDelete,
  recordSelected,
}) => {
  const isSelected = (name: string) => selectedRecordsForDelete.indexOf(name) !== -1;

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    event.stopPropagation();
    const selectedIndex = selectedRecordsForDelete.indexOf(name);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRecordsForDelete, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRecordsForDelete.slice(1));
    } else if (selectedIndex === selectedRecordsForDelete.length - 1) {
      newSelected = newSelected.concat(selectedRecordsForDelete.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedRecordsForDelete.slice(0, selectedIndex), selectedRecordsForDelete.slice(selectedIndex + 1));
    }

    setSelectedRecordsForDelete(newSelected);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data?.reduce((acc, cur) => acc.concat(cur.records.map((record) => record.id)), [] as string[]) ?? [];
      setSelectedRecordsForDelete(newSelected);
      return;
    }
    setSelectedRecordsForDelete([]);
  };

  const rowCount = data?.reduce((acc, cur) => acc + cur.records.length, 0) ?? 0;

  return (
    <>
      <TableContainer component={Paper}>
        <Table style={{ borderCollapse: 'separate' }}>
          <TableHead>
            {
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selectedRecordsForDelete.length > 0 && selectedRecordsForDelete.length < rowCount}
                    checked={rowCount > 0 && selectedRecordsForDelete.length === rowCount}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>AssetId</TableCell>
                <TableCell>Recordname</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            }
          </TableHead>
          <TableBody>
            {data?.map((row, index) => (
              // key required but not possible
              <Fragment key={index}>
                <TableRow key={row.year}>
                  <TableCell>
                    <Typography variant="h6">{row.year}</Typography>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
                {row.records.map((record) => {
                  const isItemSelected = isSelected(record.id);
                  return (
                    <TableRow
                      key={record.id}
                      hover
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                      onClick={() => recordSelected(record)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox onClick={(event) => handleClick(event, record.id)} color="primary" checked={isItemSelected} />
                      </TableCell>
                      <TableCell>{record.assetId}</TableCell>
                      <TableCell>{record.title}</TableCell>
                      <TableCell>{record.description}</TableCell>
                      <TableCell>{format(new Date(record.createdAt), 'dd/MM/yyyy')}</TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 15, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(event.target.value as any as number);
        }}
        onPageChange={(event, newPage) => {
          setPage(newPage);
        }}
      />
    </>
  );
};
